import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X } from 'lucide-react';
import { useRegistration } from '../../../../contexts/registration/hooks';
import { ServiceForm } from './components/ServiceForm';
import { ServiceCard } from './components/ServiceCard';
import { Service } from './types';
import toast from 'react-hot-toast';
import { useAuth } from '../../../../contexts/auth/hooks';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';

interface BusinessStepFourProps {
  loading?: boolean;
}

export function BusinessStepFour({ loading }: BusinessStepFourProps) {
  const { updateStep, getStepData, goToStep } = useRegistration();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Service[]>(() => {
    const savedData = getStepData(4)?.services || [];
    return savedData;
  });
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const ensureBusinessExists = async (): Promise<string> => {
    let businessId = localStorage.getItem('businessId');

    if (businessId) {

      return businessId;
    }

    const step1 = getStepData(1);
    const step2 = getStepData(2);
    const step3 = getStepData(3);

    if (!step1 || !step2) {
      throw new Error('נתוני שלבים קודמים חסרים. אנא חזור לשלבים הקודמים והשלם את המידע.');
    }

    try {
      const { data: bookingLinkData, error: bookingError } = await supabase.rpc('generate_booking_link', {
        name: step2.name
      });

      if (bookingError || !bookingLinkData) {

        throw new Error('שגיאה ביצירת קישור הזמנה');
      }

      const bookingLink = bookingLinkData;

      const { data: bizData, error: bizError } = await supabase
        .from('businesses')
        .insert({
          name: step2.name,
          description: step2.description || '',
          address: step2.address || '',
          settings: JSON.parse(JSON.stringify({ theme: 'light', notifications: true })),
          type: step2.type,
          booking_link: bookingLink,
          logo_url: step2.logo_url || null
        })
        .select()
        .single();

      if (bizError || !bizData?.id) {

        throw new Error('שגיאה ביצירת העסק');
      }

      businessId = bizData.id;
      localStorage.setItem('businessId', businessId);

      if (step3?.hours) {
        const { error: hoursError } = await supabase
          .from('business_hours')
          .insert({
            business_id: businessId,
            regular_hours: step3.hours,
            special_dates: []
          });

        if (hoursError) {

        } else {

        }
      }

      if (user?.id) {
        const { data: existingUser } = await supabase
          .from('users')
          .select('id, email')
          .eq('id', user.id)
          .maybeSingle();

        if (existingUser) {
          const { error: updateErr } = await supabase
            .from('users')
            .update({ business_id: businessId })
            .eq('id', user.id);

          if (updateErr) {

          } else {

          }
        } else {
          const { error: insertErr } = await supabase
            .from('users')
            .insert({
              id: user.id,
              email: user.email || '',
              phone: user.user_metadata?.phone || '',
              business_id: businessId,
              role: 'admin',
              completed: 0,
              metadata: {
                full_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0]
              }
            });

          if (insertErr) {

          } else {

          }
        }
      }

      return businessId;
    } catch (error: any) {

      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loading) {

      return;
    }

    if (formData.length === 0) {

      setErrors({ form: 'יש להוסיף לפחות שירות אחד' });
      toast.error('יש להוסיף לפחות שירות אחד');
      return;
    }

    try {
      const businessId = await ensureBusinessExists();

      // שמור את השירותים בלוקאל (context)
      await updateStep(4, { services: formData });

      // מחק שירותים קיימים למנוע כפילויות
      await supabase
        .from('services')
        .delete()
        .eq('business_id', businessId);

      // שמור את השירותים למסד הנתונים ישירות
      const servicesForDb = formData.map(service => ({
        business_id: businessId,
        name: service.name_he,
        name_he: service.name_he,
        price: parseFloat(service.price || '0'),
        duration: `${service.duration || '30'} minutes`,
        description: service.description || ''
      }));

      const { error: servicesError } = await supabase
        .from('services')
        .insert(servicesForDb);

      if (servicesError) {
        console.error('Error saving services:', servicesError);
        throw new Error('שגיאה בשמירת השירותים למסד הנתונים');
      }

      toast.success('השירותים נשמרו בהצלחה!');
      goToStep(5);
    } catch (error: any) {
      toast.error(error.message || 'שגיאה בשמירת השירותים');
    }
  };

  const handleServiceSubmit = (serviceData: Service) => {

    if (editingService) {
      setFormData(prev => prev.map(service =>
        service.id === editingService.id ? {
          ...serviceData,
          name: serviceData.name_he,
          name_he: serviceData.name_he
        } : service
      ));
    } else {
      setFormData(prev => [...prev, {
        ...serviceData,
        name: serviceData.name_he,
        name_he: serviceData.name_he,
        id: crypto.randomUUID()
      }]);
    }
    setShowForm(false);
    setEditingService(null);
    setErrors({});
  };

  const handleEdit = (service: Service) => {

    setEditingService(service);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {

    setFormData(prev => prev.filter(service => service.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-lg text-gray-900">השירותים שלך</h3>
          <p className="text-sm text-gray-500">{formData.length} שירותים</p>
        </div>

        {!showForm && (
          <button
            type="button"
            onClick={() => {

              setEditingService(null);
              setShowForm(true);
            }}
            className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 active:scale-95 transition-all text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>הוסף שירות</span>
          </button>
        )}
      </div>

      {/* Service Form */}
      <AnimatePresence>
        {showForm && (
          <ServiceForm
            formData={editingService || {
              name: '',
              name_he: '',
              price: '',
              duration: '30',
              description: '',
              promotion: undefined
            }}
            services={[]}
            isEditing={!!editingService}
            onFormChange={() => {}}
            onSubmit={handleServiceSubmit}
            onCancel={() => {

              setShowForm(false);
              setEditingService(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Services Grid or Empty State */}
      {formData.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {formData.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      ) : !showForm && (
        <div className="text-center py-8 px-4 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
          <p className="text-sm text-gray-500">
            לחץ על "הוסף שירות" כדי להתחיל
          </p>
        </div>
      )}

      {errors.form && (
        <p className="text-[13px] text-red-600 text-center bg-red-50 p-3 rounded-lg">
          {errors.form}
        </p>
      )}

      {/* Submit Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="pt-4"
      >
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || formData.length === 0}
          className="w-full py-3 px-4 rounded-xl text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>טוען...</span>
            </>
          ) : (
            <>
              <span>סיים והמשך</span>
              <span>←</span>
            </>
          )}
        </button>
      </motion.div>
    </div>
  );
}
